import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  TextInput,
  Platform,
} from 'react-native';
import { FIREBASE_AUTH } from '../../FirebaseConfig';
import moment from 'moment-timezone';

const AdminPaystubUpload = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [paystubs, setPaystubs] = useState([]);
  const [uploadMode, setUploadMode] = useState('batch'); // 'single', 'batch', or 'manage'
  const [downloadingId, setDownloadingId] = useState(null);
  const [formData, setFormData] = useState({
    payPeriodStart: '',
    payPeriodEnd: '',
    weekNumber: '',
    year: moment().year().toString(),
    totalHours: '',
    grossPay: '',
    netPay: '',
    deductions: '',
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const user = FIREBASE_AUTH.currentUser;
      if (!user) {
        Alert.alert('Error', 'Please log in as admin');
        return;
      }

      const token = await user.getIdToken();
      const response = await fetch(`${process.env.BACKEND_URL || 'https://sbg-backend.onrender.com'}/employees`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch employees');
      }

      const data = await response.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      Alert.alert('Error', 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPaystubs = async () => {
    try {
      setLoading(true);
      const user = FIREBASE_AUTH.currentUser;
      const token = await user.getIdToken();
      
      const response = await fetch(`https://sbg-backend.onrender.com/api/admin/paystubs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch paystubs');
      }

      const data = await response.json();
      setPaystubs(data.paystubs || []);
    } catch (error) {
      console.error('Error fetching paystubs:', error);
      Alert.alert('Error', 'Failed to load paystubs');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupPaystubs = async () => {
    console.log('=== CLEANUP BUTTON CLICKED ===');
    
    // Skip the first alert for now and directly proceed to deletion
    try {
      console.log('Starting cleanup process...');
      alert('About to start cleanup process!');
      setLoading(true);
      const user = FIREBASE_AUTH.currentUser;
      
      if (!user) {
        throw new Error('No authenticated user found');
      }
      
      const token = await user.getIdToken();
      console.log('Got auth token, making request...');
      alert('Making API request...');
      
      const response = await fetch(`https://sbg-backend.onrender.com/api/admin/paystubs/cleanup-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status, response.statusText);
      alert(`Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        alert(`Error: ${JSON.stringify(errorData)}`);
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to cleanup paystubs`);
      }

      const data = await response.json();
      console.log('Cleanup response:', data);
      alert(`Success: ${JSON.stringify(data.results)}`);
      
      Alert.alert('Success', `Cleanup completed! Deleted ${data.results.databaseRecordsDeleted} records and ${data.results.storageFilesDeleted} files.`);
      
      // Reset state and refresh the list
      setPaystubs([]);
      setBatchResults(null);
      
      // If we're in manage mode, refresh the list to show empty state
      if (uploadMode === 'manage') {
        fetchAllPaystubs();
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      alert(`Cleanup failed: ${error.message}`);
      Alert.alert('Error', `Cleanup failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDownloadPaystub = async (paystub) => {
    try {
      setDownloadingId(paystub._id);
      const user = FIREBASE_AUTH.currentUser;
      const token = await user.getIdToken();

      const response = await fetch(`https://sbg-backend.onrender.com/api/paystubs/${paystub._id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate download link');
      }

      const data = await response.json();
      
      // For web, open in new tab
      if (Platform.OS === 'web') {
        window.open(data.downloadUrl, '_blank');
      } else {
        // For mobile, you might want to use a WebView or PDF viewer
        Alert.alert('Download Ready', `Paystub for ${paystub.employeeName} is ready for viewing`, [
          {
            text: 'Open',
            onPress: () => {
              // Implement mobile PDF viewing logic here
              console.log('Open PDF:', data.downloadUrl);
            }
          }
        ]);
      }
    } catch (error) {
      console.error('Error downloading paystub:', error);
      Alert.alert('Error', 'Failed to open paystub. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleFileSelect = () => {
    // For web platform
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf';
      input.onchange = (event) => {
        const file = event.target.files[0];
        if (file) {
          if (uploadMode === 'batch') {
            handleBatchUpload(file);
          } else {
            handleUpload(file);
          }
        }
      };
      input.click();
    } else {
      // For mobile platforms, you would use react-native-document-picker
      Alert.alert('Mobile Upload', 'File upload on mobile requires react-native-document-picker');
    }
  };

  const handleBatchUpload = async (file) => {
    if (!formData.payPeriodStart || !formData.payPeriodEnd) {
      Alert.alert('Error', 'Please fill in the pay period dates');
      return;
    }

    try {
      setBatchProcessing(true);
      const user = FIREBASE_AUTH.currentUser;
      const token = await user.getIdToken();

      const uploadFormData = new FormData();
      uploadFormData.append('batchPaystub', file);
      uploadFormData.append('payPeriodStart', formData.payPeriodStart);
      uploadFormData.append('payPeriodEnd', formData.payPeriodEnd);
      uploadFormData.append('weekNumber', formData.weekNumber);
      uploadFormData.append('year', formData.year);
      uploadFormData.append('notifyEmployees', 'false'); // TESTING MODE - No emails

      const response = await fetch(`${process.env.BACKEND_URL || 'https://sbg-backend.onrender.com'}/api/admin/paystubs/batch-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Batch upload failed');
      }

      const result = await response.json();
      setBatchResults(result.results);
      Alert.alert('Success', `Batch processing completed! Successfully processed ${result.results.savedPaystubs} paystubs.`);
      
      // Reset form but keep results for review
      setFormData({
        payPeriodStart: '',
        payPeriodEnd: '',
        weekNumber: '',
        year: moment().year().toString(),
        totalHours: '',
        grossPay: '',
        netPay: '',
        deductions: '',
      });
    } catch (error) {
      console.error('Batch upload error:', error);
      Alert.alert('Error', error.message || 'Failed to process batch paystubs');
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleUpload = async (file) => {
    if (!selectedEmployee) {
      Alert.alert('Error', 'Please select an employee first');
      return;
    }

    if (!formData.payPeriodStart || !formData.payPeriodEnd) {
      Alert.alert('Error', 'Please fill in the pay period dates');
      return;
    }

    try {
      setUploading(true);
      const user = FIREBASE_AUTH.currentUser;
      const token = await user.getIdToken();

      const uploadFormData = new FormData();
      uploadFormData.append('paystub', file);
      uploadFormData.append('employeeId', selectedEmployee.employeeId);
      uploadFormData.append('payPeriodStart', formData.payPeriodStart);
      uploadFormData.append('payPeriodEnd', formData.payPeriodEnd);
      uploadFormData.append('weekNumber', formData.weekNumber);
      uploadFormData.append('year', formData.year);
      uploadFormData.append('totalHours', formData.totalHours);
      uploadFormData.append('grossPay', formData.grossPay);
      uploadFormData.append('netPay', formData.netPay);
      uploadFormData.append('deductions', formData.deductions);

      const response = await fetch(`${process.env.BACKEND_URL || 'https://sbg-backend.onrender.com'}/api/admin/paystubs/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      Alert.alert('Success', `Paystub uploaded successfully for ${result.employee}`);
      
      // Reset form
      setSelectedEmployee(null);
      setFormData({
        payPeriodStart: '',
        payPeriodEnd: '',
        weekNumber: '',
        year: moment().year().toString(),
        totalHours: '',
        grossPay: '',
        netPay: '',
        deductions: '',
      });
    } catch (error) {
      console.error('Upload error:', error);
      Alert.alert('Error', error.message || 'Failed to upload paystub');
    } finally {
      setUploading(false);
    }
  };

  const calculateWeekNumber = (startDate) => {
    if (startDate) {
      const week = moment(startDate).week();
      setFormData(prev => ({
        ...prev,
        weekNumber: week.toString(),
        year: moment(startDate).year().toString(),
      }));
    }
  };

  const handleStartDateChange = (date) => {
    setFormData(prev => ({ ...prev, payPeriodStart: date }));
    calculateWeekNumber(date);
    
    // Auto-calculate end date (6 days later)
    if (date) {
      const endDate = moment(date).add(6, 'days').format('YYYY-MM-DD');
      setFormData(prev => ({ ...prev, payPeriodEnd: endDate }));
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loadingText}>Loading employees...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Upload Paystubs</Text>
        <Text style={styles.headerSubtitle}>Admin Panel</Text>
        
        {/* Upload Mode Toggle */}
        <View style={styles.toggleContainer}>
          <TouchableOpacity
            style={[styles.toggleButton, uploadMode === 'single' && styles.toggleButtonActive]}
            onPress={() => setUploadMode('single')}
          >
            <Text style={[styles.toggleText, uploadMode === 'single' && styles.toggleTextActive]}>
              Single
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleButton, uploadMode === 'batch' && styles.toggleButtonActive]}
            onPress={() => setUploadMode('batch')}
          >
            <Text style={[styles.toggleText, uploadMode === 'batch' && styles.toggleTextActive]}>
              Batch
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleButton, uploadMode === 'manage' && styles.toggleButtonActive]}
            onPress={() => {
              setUploadMode('manage');
              fetchAllPaystubs();
            }}
          >
            <Text style={[styles.toggleText, uploadMode === 'manage' && styles.toggleTextActive]}>
              View All
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {uploadMode === 'batch' && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Batch Processing Info</Text>
          <View style={styles.infoBox}>
            <Text style={styles.infoText}>
              Upload a single PDF containing multiple paystubs (~125 pages). The system will automatically:
            </Text>
            <Text style={styles.bulletPoint}>• Split PDF into individual paystubs</Text>
            <Text style={styles.bulletPoint}>• Match each paystub to correct employee using AI</Text>
            <Text style={styles.bulletPoint}>• Save individual files to secure storage</Text>
            <Text style={styles.bulletPoint}>• Send notifications to all employees</Text>
            <Text style={styles.bulletPoint}>• Generate detailed processing report</Text>
          </View>
        </View>
      )}

      {/* Single Upload Mode */}
      {uploadMode === 'single' && (
        <>
          {/* Employee Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Select Employee</Text>
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={styles.employeeScrollView}
            >
              {employees.map((employee) => (
                <TouchableOpacity
                  key={employee.employeeId}
                  style={[
                    styles.employeeCard,
                    selectedEmployee?.employeeId === employee.employeeId && styles.selectedEmployeeCard
                  ]}
                  onPress={() => setSelectedEmployee(employee)}
                >
                  <Text style={[
                    styles.employeeName,
                    selectedEmployee?.employeeId === employee.employeeId && styles.selectedEmployeeName
                  ]}>
                    {employee.employeeName}
                  </Text>
                  <Text style={[
                    styles.employeeId,
                    selectedEmployee?.employeeId === employee.employeeId && styles.selectedEmployeeId
                  ]}>
                    ID: {employee.employeeId}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {selectedEmployee && (
            <>
              {/* Pay Period Information */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Pay Period Information</Text>
                
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Pay Period Start Date</Text>
                  <TextInput
                    style={styles.input}
                    value={formData.payPeriodStart}
                    onChangeText={handleStartDateChange}
                    placeholder="YYYY-MM-DD"
                    placeholderTextColor="#999"
                  />
                </View>

                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Pay Period End Date</Text>
                  <TextInput
                    style={styles.input}
                    value={formData.payPeriodEnd}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, payPeriodEnd: text }))}
                    placeholder="YYYY-MM-DD"
                    placeholderTextColor="#999"
                  />
                </View>

                <View style={styles.row}>
                  <View style={[styles.inputGroup, styles.halfWidth]}>
                    <Text style={styles.inputLabel}>Week Number</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.weekNumber}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, weekNumber: text }))}
                      placeholder="Week #"
                      placeholderTextColor="#999"
                      keyboardType="numeric"
                    />
                  </View>

                  <View style={[styles.inputGroup, styles.halfWidth]}>
                    <Text style={styles.inputLabel}>Year</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.year}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, year: text }))}
                      placeholder="2024"
                      placeholderTextColor="#999"
                      keyboardType="numeric"
                    />
                  </View>
                </View>
              </View>

              {/* Pay Information (Optional) */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Pay Information (Optional)</Text>
                
                <View style={styles.row}>
                  <View style={[styles.inputGroup, styles.halfWidth]}>
                    <Text style={styles.inputLabel}>Total Hours</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.totalHours}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, totalHours: text }))}
                      placeholder="40.0"
                      placeholderTextColor="#999"
                      keyboardType="decimal-pad"
                    />
                  </View>

                  <View style={[styles.inputGroup, styles.halfWidth]}>
                    <Text style={styles.inputLabel}>Gross Pay</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.grossPay}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, grossPay: text }))}
                      placeholder="1000.00"
                      placeholderTextColor="#999"
                      keyboardType="decimal-pad"
                    />
                  </View>
                </View>

                <View style={styles.row}>
                  <View style={[styles.inputGroup, styles.halfWidth]}>
                    <Text style={styles.inputLabel}>Net Pay</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.netPay}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, netPay: text }))}
                      placeholder="800.00"
                      placeholderTextColor="#999"
                      keyboardType="decimal-pad"
                    />
                  </View>

                  <View style={[styles.inputGroup, styles.halfWidth]}>
                    <Text style={styles.inputLabel}>Deductions</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.deductions}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, deductions: text }))}
                      placeholder="200.00"
                      placeholderTextColor="#999"
                      keyboardType="decimal-pad"
                    />
                  </View>
                </View>
              </View>

              {/* Upload Button */}
              <View style={styles.section}>
                <TouchableOpacity
                  style={[styles.uploadButton, uploading && styles.uploadButtonDisabled]}
                  onPress={handleFileSelect}
                  disabled={uploading}
                >
                  {uploading ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.uploadButtonText}>Select and Upload PDF</Text>
                  )}
                </TouchableOpacity>

                <Text style={styles.uploadHint}>
                  Only PDF files are allowed. File will be securely stored and employee will be notified.
                </Text>
              </View>
            </>
          )}
        </>
      )}

      {/* Batch Upload Mode */}
      {uploadMode === 'batch' && (
        <>
          {/* Pay Period Information for Batch */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Pay Period Information</Text>
            
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Pay Period Start Date *</Text>
              <TextInput
                style={styles.input}
                value={formData.payPeriodStart}
                onChangeText={handleStartDateChange}
                placeholder="YYYY-MM-DD"
                placeholderTextColor="#999"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Pay Period End Date *</Text>
              <TextInput
                style={styles.input}
                value={formData.payPeriodEnd}
                onChangeText={(text) => setFormData(prev => ({ ...prev, payPeriodEnd: text }))}
                placeholder="YYYY-MM-DD"
                placeholderTextColor="#999"
              />
            </View>

            <View style={styles.row}>
              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.inputLabel}>Week Number</Text>
                <TextInput
                  style={styles.input}
                  value={formData.weekNumber}
                  onChangeText={(text) => setFormData(prev => ({ ...prev, weekNumber: text }))}
                  placeholder="Auto-calculated"
                  placeholderTextColor="#999"
                  keyboardType="numeric"
                />
              </View>

              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.inputLabel}>Year</Text>
                <TextInput
                  style={styles.input}
                  value={formData.year}
                  onChangeText={(text) => setFormData(prev => ({ ...prev, year: text }))}
                  placeholder="2024"
                  placeholderTextColor="#999"
                  keyboardType="numeric"
                />
              </View>
            </View>
          </View>

          {/* Batch Upload Button */}
          <View style={styles.section}>
            <TouchableOpacity
              style={[styles.batchUploadButton, (batchProcessing || uploading) && styles.uploadButtonDisabled]}
              onPress={handleFileSelect}
              disabled={batchProcessing || uploading}
            >
              {batchProcessing ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={styles.batchUploadButtonText}>Process Batch PDF (125+ Paystubs)</Text>
              )}
            </TouchableOpacity>

            <Text style={styles.uploadHint}>
              Upload encrypted PDF with all employee paystubs. Processing may take several minutes.
            </Text>
          </View>

          {/* Batch Results Display */}
          {batchResults && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Batch Processing Results</Text>
              
              <View style={styles.resultsGrid}>
                <View style={styles.resultCard}>
                  <Text style={styles.resultNumber}>{batchResults.savedPaystubs}</Text>
                  <Text style={styles.resultLabel}>Processed</Text>
                </View>
                <View style={styles.resultCard}>
                  <Text style={styles.resultNumber}>{batchResults.unmatched?.length || 0}</Text>
                  <Text style={styles.resultLabel}>Unmatched</Text>
                </View>
                <View style={styles.resultCard}>
                  <Text style={styles.resultNumber}>{batchResults.missingEmployees?.length || 0}</Text>
                  <Text style={styles.resultLabel}>Missing</Text>
                </View>
                <View style={styles.resultCard}>
                  <Text style={styles.resultNumber}>{batchResults.notifications || 0}</Text>
                  <Text style={styles.resultLabel}>Notified</Text>
                </View>
              </View>

              <TouchableOpacity
                style={styles.clearResultsButton}
                onPress={() => setBatchResults(null)}
              >
                <Text style={styles.clearResultsText}>Clear Results</Text>
              </TouchableOpacity>
            </View>
          )}
        </>
      )}

      {/* View/Manage Mode */}
      {uploadMode === 'manage' && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>All Paystubs ({paystubs.length})</Text>
          
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#0066cc" />
              <Text style={styles.loadingText}>Loading paystubs...</Text>
            </View>
          ) : paystubs.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No paystubs found</Text>
            </View>
          ) : (
            <ScrollView style={styles.paystubsList}>
              {paystubs.map((paystub) => (
                <View key={paystub._id} style={styles.paystubItem}>
                  <View style={styles.paystubHeader}>
                    <Text style={styles.employeeNameInList}>{paystub.employeeName}</Text>
                    <Text style={styles.employeeIdInList}>ID: {paystub.employeeId}</Text>
                  </View>
                  <View style={styles.paystubDetails}>
                    <Text style={styles.payPeriod}>
                      {formatDate(paystub.payPeriodStart)} - {formatDate(paystub.payPeriodEnd)}
                    </Text>
                    <Text style={styles.weekInfo}>Week {paystub.weekNumber}, {paystub.year}</Text>
                    <View style={styles.statusRow}>
                      <View style={[styles.statusBadge,
                        paystub.status === 'available' && styles.statusAvailable
                      ]}>
                        <Text style={[styles.statusText,
                          paystub.status === 'available' && styles.statusTextAvailable
                        ]}>
                          {paystub.status}
                        </Text>
                      </View>
                      <Text style={styles.uploadDate}>
                        {formatDate(paystub.uploadedAt)}
                      </Text>
                    </View>
                    
                    <View style={styles.paystubButtonRow}>
                      <TouchableOpacity
                        style={[styles.downloadButton, downloadingId === paystub._id && styles.downloadButtonDisabled]}
                        onPress={() => handleDownloadPaystub(paystub)}
                        disabled={downloadingId === paystub._id}
                      >
                        {downloadingId === paystub._id ? (
                          <ActivityIndicator size="small" color="#ffffff" />
                        ) : (
                          <Text style={styles.downloadButtonText}>View PDF</Text>
                        )}
                      </TouchableOpacity>
                      
                      <TouchableOpacity
                        style={styles.deleteButton}
                        onPress={() => handleDeletePaystub(paystub)}
                      >
                        <Text style={styles.deleteButtonText}>🗑️</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              ))}
            </ScrollView>
          )}
          
          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={styles.refreshButton}
              onPress={fetchAllPaystubs}
            >
              <Text style={styles.refreshButtonText}>Refresh List</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.cleanupButton}
              onPress={handleCleanupPaystubs}
            >
              <Text style={styles.cleanupButtonText}>🗑️ Delete All Test Data</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.cleanupButton, { backgroundColor: '#28a745' }]}
              onPress={() => {
                console.log('Test button clicked!');
                alert('Test button works!');
              }}
            >
              <Text style={styles.cleanupButtonText}>🧪 Test Button</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  contentContainer: {
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  header: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#003366',
  },
  headerSubtitle: {
    fontSize: 16,
    color: '#666',
    marginTop: 4,
  },
  toggleContainer: {
    flexDirection: 'row',
    marginTop: 15,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    padding: 4,
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignItems: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#0066cc',
  },
  toggleText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  toggleTextActive: {
    color: '#ffffff',
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#003366',
    marginBottom: 15,
  },
  infoBox: {
    backgroundColor: '#e8f4fd',
    padding: 15,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#0066cc',
  },
  infoText: {
    fontSize: 14,
    color: '#333',
    marginBottom: 10,
  },
  bulletPoint: {
    fontSize: 14,
    color: '#666',
    marginLeft: 10,
    marginBottom: 5,
  },
  employeeScrollView: {
    maxHeight: 120,
  },
  employeeCard: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginRight: 10,
    minWidth: 150,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedEmployeeCard: {
    backgroundColor: '#e8f4fd',
    borderColor: '#0066cc',
  },
  employeeName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#003366',
  },
  selectedEmployeeName: {
    color: '#0066cc',
  },
  employeeId: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  selectedEmployeeId: {
    color: '#0066cc',
  },
  inputGroup: {
    marginBottom: 15,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#003366',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  halfWidth: {
    flex: 0.48,
  },
  uploadButton: {
    backgroundColor: '#0066cc',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  uploadButtonDisabled: {
    backgroundColor: '#ccc',
  },
  uploadButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '600',
  },
  batchUploadButton: {
    backgroundColor: '#16a085',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  batchUploadButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '600',
  },
  uploadHint: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  resultsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  resultCard: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  resultNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#003366',
  },
  resultLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  clearResultsButton: {
    backgroundColor: '#6c757d',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 15,
  },
  clearResultsText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  paystubsList: {
    maxHeight: 400,
  },
  paystubItem: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#0066cc',
  },
  paystubHeader: {
    marginBottom: 8,
  },
  employeeNameInList: {
    fontSize: 16,
    fontWeight: '600',
    color: '#003366',
  },
  employeeIdInList: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  paystubDetails: {
    gap: 4,
  },
  payPeriod: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  weekInfo: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: '#e0e0e0',
  },
  statusAvailable: {
    backgroundColor: '#e8f5e8',
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#666',
    textTransform: 'capitalize',
  },
  statusTextAvailable: {
    color: '#2e7d2e',
  },
  uploadDate: {
    fontSize: 11,
    color: '#888',
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  refreshButton: {
    backgroundColor: '#0066cc',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 15,
  },
  refreshButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  paystubButtonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },
  downloadButton: {
    backgroundColor: '#0066cc',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    flex: 1,
    alignItems: 'center',
    marginRight: 8,
  },
  downloadButtonDisabled: {
    backgroundColor: '#ccc',
  },
  downloadButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
  },
  deleteButtonText: {
    fontSize: 16,
  },
  cleanupButton: {
    backgroundColor: '#dc3545',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 15,
    marginLeft: 10,
  },
  cleanupButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default AdminPaystubUpload;